import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Smart Context Detection Hook
 * 
 * Detects current page context, analysis state, and available data
 * to provide intelligent context for the SEO Assistant chat.
 * 
 * @param {Object} appData - Main app data from App.jsx
 * @param {Object} authStatus - User authentication status
 * @returns {Object} Smart context object with type, data, and summary
 */
export const useSmartContext = (appData, authStatus) => {
  const location = useLocation();
  const [contextHistory, setContextHistory] = useState([]);
  const [urlParams, setUrlParams] = useState(new URLSearchParams(location.search));
  
  // Update urlParams when location.search changes (for route changes)
  useEffect(() => {
    setUrlParams(new URLSearchParams(location.search));
  }, [location.search]);
  
  // Listen for URL parameter changes (for tab switches within same route)
  useEffect(() => {
    const handleUrlChange = () => {
      const newParams = new URLSearchParams(window.location.search);
      setUrlParams(newParams);
    };
    
    // Listen for popstate (back/forward) and pushstate/replacestate events
    window.addEventListener('popstate', handleUrlChange);
    
    // Override window.history methods to catch programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleUrlChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleUrlChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);
  
  // Detect current context based on route and available data
  const currentContext = useMemo(() => {
    const pathname = location.pathname;
    const searchParams = urlParams; // Use our manually tracked URL params
    
    // Helper to get current analysis data
    const getAnalysisData = () => {
      return appData?.temporaryAnalysis || null;
    };
    
    // Helper function to get formatted date periods from analysis
    const getAnalysisDatePeriods = () => {
      const analysis = getAnalysisData();
      const dateRange = analysis?.meta?.dateRange;
      
      console.log('🗓️ [SmartContext] Analysis date extraction:', {
        hasAnalysis: !!analysis,
        hasMeta: !!analysis?.meta,
        hasDateRange: !!dateRange,
        dateRange: dateRange,
        periodOne: dateRange?.periodOne,
        periodTwo: dateRange?.periodTwo
      });
      
      if (!dateRange?.periodOne || !dateRange?.periodTwo) {
        console.log('🗓️ [SmartContext] Missing date periods - returning null');
        return null;
      }
      
      const result = {
        oldPeriod: {
          startDate: dateRange.periodOne.startDate,
          endDate: dateRange.periodOne.endDate,
          formatted: `${dateRange.periodOne.startDate} to ${dateRange.periodOne.endDate}`
        },
        newPeriod: {
          startDate: dateRange.periodTwo.startDate,
          endDate: dateRange.periodTwo.endDate,
          formatted: `${dateRange.periodTwo.startDate} to ${dateRange.periodTwo.endDate}`
        },
        comparison: `Comparing ${dateRange.periodTwo.startDate} to ${dateRange.periodTwo.endDate} vs ${dateRange.periodOne.startDate} to ${dateRange.periodOne.endDate}`
      };
      
      console.log('🗓️ [SmartContext] Formatted date periods:', result);
      return result;
    };
    
    // Helper to get current property info
    const getPropertyInfo = () => {
      return {
        current: appData?.selectedProperty || null,
        available: appData?.properties || [],
        isConnected: appData?.isConnected || false
      };
    };
    
    // Helper to extract page URL from query params
    const getPageFromParams = () => {
      return searchParams.get('page') || searchParams.get('url') || null;
    };

    // 📖 TUTORIAL PORTAL CONTEXT - detect via DOM presence
    const tutorialPortal = document.querySelector('[data-tutorial-portal]');
    if (tutorialPortal) {
      // Extract tutorial data from DOM attributes
      const tutorialTitle = tutorialPortal.getAttribute('data-tutorial-title') || 'Interactive Tutorial';
      const tutorialPageUrl = tutorialPortal.getAttribute('data-tutorial-page-url');
      
      return {
        type: 'tutorial_interaction',
        route: null, // No route - it's a portal
        page: tutorialPageUrl,
        data: {
          tutorialTitle: tutorialTitle,
          portalElement: tutorialPortal,
          // Additional data can be extracted from DOM or data attributes
        },
        summary: `Interactive Tutorial: "${tutorialTitle}"`,
        capabilities: [
          'Tutorial content refinement',
          'Implementation guidance',
          'Platform-specific customization',
          'Task workflow integration'
        ]
      };
    }
    
    // 📝 CONTENT STRATEGY CONTEXT
    if (pathname.includes('/content-strategy')) {
      // Check if this is the article editor
      const articleEditorMatch = pathname.match(/\/content-strategy\/article\/(\d+)\/edit/);
      
      if (articleEditorMatch) {
        const articleId = articleEditorMatch[1];
        return {
          type: 'content_strategy_editor',
          route: pathname,
          page: null,
          data: {
            articleId: articleId,
            // Note: Actual article data will be captured via content strategy context builder
          },
          summary: `Content Strategy Editor - editing article #${articleId}`,
          capabilities: [
            'Content strategy guidance',
            'SEO content optimization',
            'Editorial assistance',
            'Keyword targeting advice',
            'Content structure analysis',
            'Publishing workflow guidance'
          ]
        };
      } else {
        // Content strategy dashboard
        return {
          type: 'content_strategy_dashboard',
          route: pathname,
          page: null,
          data: {
            // Dashboard context will be captured via content strategy context builder
          },
          summary: 'Content Strategy Dashboard - managing content pipeline',
          capabilities: [
            'Content strategy planning',
            'Editorial pipeline management',
            'Content idea generation',
            'Priority optimization',
            'Publishing workflow guidance',
            'Performance analysis'
          ]
        };
      }
    }
    
    // 📍 KEYWORDS ANALYSIS CONTEXT
    if (pathname === '/keywords' || pathname.startsWith('/keywords')) {
      const analysisData = getAnalysisData();
      const activeFilter = searchParams.get('filter') || 'all';
      const datePeriods = getAnalysisDatePeriods();
      
      return {
        type: 'keywords_analysis',
        page: null,
        route: '/keywords',
        subType: activeFilter,
        data: {
          analysis: analysisData,
          activeFilter,
          hasData: !!analysisData,
          datePeriods,
          totalKeywords: analysisData?.analysis ? Object.values(analysisData.analysis).reduce((sum, page) => sum + (page.keywords?.length || 0), 0) : 0
        },
        summary: `Keywords analysis - ${activeFilter} keywords view${datePeriods ? ` (${datePeriods.comparison})` : ''}`,
        capabilities: [
          'Keyword performance analysis',
          'Ranking position tracking',
          'Search volume insights',
          'Keyword opportunity identification'
        ]
      };
    }
    
    // 📄 PAGES ANALYSIS CONTEXT  
    if (pathname === '/analysis' || pathname.startsWith('/analysis')) {
      const analysisData = getAnalysisData();
      const activeFilter = searchParams.get('filter') || 'all';
      const datePeriods = getAnalysisDatePeriods();
      
      return {
        type: 'pages_analysis', 
        page: null,
        route: '/analysis',
        subType: activeFilter,
        data: {
          analysis: analysisData,
          activeFilter,
          hasData: !!analysisData,
          datePeriods,
          totalPages: analysisData?.analysis ? Object.keys(analysisData.analysis).length : 0
        },
        summary: `Pages analysis - ${activeFilter} pages view${datePeriods ? ` (${datePeriods.comparison})` : ''}`,
        capabilities: [
          'Page performance comparison',
          'Traffic distribution analysis', 
          'Page optimization opportunities',
          'Content performance insights'
        ]
      };
    }
    
    // 📈 Historical/Performance Context (MUST BE BEFORE GENERAL PERFORMANCE CHECK)
    if (pathname.includes('/performance') || pathname.includes('/historical')) {
      const propertyInfo = getPropertyInfo();
      const pathParts = pathname.split('/');
      const domain = pathParts[2]; // /performance/domain.com/historical
      const reportType = pathParts[3] || 'overview'; // historical, yoy, or overview
      
      // Simple context - let backend fetch the actual data
      const contextData = {
        domain: domain,
        reportType: reportType,
        property: propertyInfo
      };
      
      console.log('🔍 [Historical Performance Context] Sending basic route info:', {
        pathname,
        domain,
        reportType,
        propertyInfo
      });
      
      return {
        type: 'historical_performance',
        page: null,
        route: pathname,
        subType: reportType,
        data: contextData,
        summary: domain 
          ? `Historical performance for ${domain}`
          : `Historical performance overview - ${propertyInfo.current || 'your website'}`,
        capabilities: [
          'Analyze page performance trends from database',
          'Identify declining pages',
          'Spot growth opportunities', 
          'Navigate to specific page analysis',
          'Compare page performance patterns',
          'Recommend pages for deeper investigation'
        ]
      };
    }

    // ⚡ LIGHTHOUSE/PERFORMANCE CONTEXT (Only for non-historical performance routes)
    if (pathname === '/lighthouse' || pathname.startsWith('/lighthouse')) {
      return {
        type: 'performance_analysis',
        page: null, 
        route: pathname,
        subType: 'lighthouse',
        data: {
          hasData: !!appData
        },
        summary: `Performance analysis - Core Web Vitals and page speed`,
        capabilities: [
          'Core Web Vitals analysis',
          'Page speed optimization',
          'Performance benchmarking',
          'Technical performance insights'
        ]
      };
    }
    
    // 🏠 Site Overview Context
    if (pathname === '/' || pathname === '/overview' || pathname === '/dashboard') {
      const analysisData = getAnalysisData();
      const propertyInfo = getPropertyInfo();
      
      return {
        type: 'site_overview',
        page: null,
        route: pathname,
        data: {
          analysis: analysisData,
          property: propertyInfo,
          hasData: !!analysisData,
          totalPages: analysisData?.analysis ? Object.keys(analysisData.analysis).length : 0,
          topPages: analysisData?.analysis ? 
            Object.entries(analysisData.analysis)
              .sort(([,a], [,b]) => (b.c2 || 0) - (a.c2 || 0))
              .slice(0, 5)
              .map(([url, data]) => ({ url, clicks: data.c2 || 0 })) 
            : []
        },
        summary: analysisData 
          ? `Site overview for ${propertyInfo.current} (${Object.keys(analysisData.analysis || {}).length} pages analyzed)`
          : `Site overview for ${propertyInfo.current || 'your website'}`,
        capabilities: [
          'Site-wide performance analysis',
          'Top performing pages overview', 
          'Traffic trends and changes',
          'Property management'
        ]
      };
    }
    
    // 📊 Page-Specific Analysis Context (Primary page analysis views)
    if (pathname.includes('/page-analysis/')) {
      // Extract page URL from the path (URL encoded)
      const pathParts = pathname.split('/page-analysis/');
      const encodedPageUrl = pathParts[1];
      const pageUrl = encodedPageUrl ? decodeURIComponent(encodedPageUrl) : null;
      
      // Get the current tab from URL params
      const currentTab = searchParams.get('tab') || 'overview';
      
      // Debug logging to see what we're detecting
      console.log('🔍 [SmartContext] Page Analysis Detection:', {
        pathname,
        encodedPageUrl,
        pageUrl,
        currentTab,
        allParams: Object.fromEntries(searchParams.entries())
      });
      
      // Get analysis data for this specific page
      const analysisData = getAnalysisData();
      const pageData = pageUrl && analysisData?.analysis?.[pageUrl] || null;
      const datePeriods = getAnalysisDatePeriods();
      
      // Determine specific analysis type based on tab
      const getAnalysisTypeFromTab = (tab) => {
        const tabTypes = {
          'overview': 'Page Overview',
          'keywords': 'Keyword Analysis', 
          'lighthouse': 'Lighthouse Audit',
          'search-intent': 'Search Intent Analysis',
          'quality-rater': 'Quality Rater Guidelines',
          'helpful-content': 'Helpful Content Guidelines',
          'structured-v2': 'Structured Analysis V2',
          'content-gaps': 'Content Gap Analysis',
          'seo-audit': 'SEO Audit',
          'seo-analysis': 'Virtual SEO Consultant'
        };
        return tabTypes[tab] || 'Page Analysis';
      };
      
      const analysisType = getAnalysisTypeFromTab(currentTab);
      
      // Check if this is an audit tab that might have AI summaries
      const auditTabsWithSummaries = [
        'quality-rater', 'helpful-content', 'content-gaps', 
        'structured-v2', 'search-intent'
      ];
      
      const hasAuditContext = pageUrl && auditTabsWithSummaries.includes(currentTab);
      
      return {
        type: 'page_analysis',
        page: pageUrl,
        route: `/page-analysis/${encodedPageUrl}`,
        subType: currentTab,
        data: {
          pageData,
          analysis: analysisData,
          hasPageData: !!pageData,
          currentTab,
          datePeriods,
          clicks: pageData?.c2 || 0,
          impressions: pageData?.i2 || 0,
          position: pageData?.p2 || null,
          clicksChange: pageData ? (pageData.c2 || 0) - (pageData.c1 || 0) : null,
          analysisType,
          // Audit context for agent (when viewing audit tabs)
          auditContext: hasAuditContext ? {
            auditType: currentTab.replace('-', '_'), // Convert tab name to audit type
            url: pageUrl,
            hasAuditContext: true
          } : null
        },
        summary: pageUrl 
          ? `${analysisType}: "${pageUrl}" (${pageData?.c2 || 0} clicks, position ${pageData?.p2 || 'N/A'})${datePeriods ? ` - ${datePeriods.comparison}` : ''}`
          : `${analysisType} - select a page to analyze`,
        capabilities: [
          'Page-specific performance metrics',
          'Keyword rankings and opportunities', 
          'Content optimization suggestions',
          'Technical SEO analysis',
          'Search intent analysis',
          'Quality assessment',
          'Content gap identification'
        ]
      };
    }
    
    // 📊 Legacy Page Analysis Context (for /intent-analysis routes)
    if (pathname.includes('/intent-analysis') || pathname.includes('/search-intent')) {
      const pageUrl = getPageFromParams();
      const analysisData = getAnalysisData();
      const pageData = pageUrl && analysisData?.analysis?.[pageUrl] || null;
      
      return {
        type: 'page_analysis',
        page: pageUrl,
        subType: 'search-intent',
        data: {
          pageData,
          analysis: analysisData,
          hasPageData: !!pageData,
          currentTab: 'search-intent',
          clicks: pageData?.c2 || 0,
          impressions: pageData?.i2 || 0,
          position: pageData?.p2 || null,
          clicksChange: pageData ? (pageData.c2 || 0) - (pageData.c1 || 0) : null
        },
        summary: pageUrl 
          ? `Search Intent Analysis: "${pageUrl}" (${pageData?.c2 || 0} clicks, position ${pageData?.p2 || 'N/A'})`
          : 'Search intent analysis mode - select a page to analyze',
        capabilities: [
          'Page-specific performance metrics',
          'Keyword rankings and opportunities',
          'Content optimization suggestions', 
          'Search intent analysis'
        ]
      };
    }
    
    // 🎯 AI TOOLS CONTEXT
    if (pathname === '/prompt-burst') {
      return {
        type: 'ai_tool_prompt_burst',
        page: null,
        route: '/prompt-burst',
        subType: 'query_analysis',
        data: {
          toolName: 'Prompt Burst',
          toolType: 'query_fan_out_analysis',
          currentTool: 'prompt-burst'
        },
        summary: 'Prompt Burst - AI Query Fan-Out Analysis Tool',
        capabilities: [
          'Query decomposition analysis',
          'AI search pattern understanding',
          'Content gap identification',
          'Research strategy optimization'
        ]
      };
    }
    
    if (pathname === '/ai-crawler') {
      return {
        type: 'ai_tool_ai_crawler',
        page: null,
        route: '/ai-crawler',
        subType: 'concept_analysis',
        data: {
          toolName: 'AI Crawler',
          toolType: 'web_concept_analysis',
          currentTool: 'ai-crawler'
        },
        summary: 'AI Crawler - Web Concept Analysis Tool',
        capabilities: [
          'Web content concept extraction',
          'AI comprehension analysis',
          'Content optimization recommendations',
          'Missing concept identification'
        ]
      };
    }
    
    // 🔧 Tools Context
    if (pathname.includes('/tools')) {
      const toolType = pathname.split('/tools/')[1] || 'general';
      const targetUrl = getPageFromParams();
      
      return {
        type: 'tools_analysis',
        page: targetUrl,
        data: {
          toolType,
          targetUrl,
          availableTools: ['lighthouse', 'structured-data', 'meta-tags', 'robots']
        },
        summary: targetUrl 
          ? `Running ${toolType} analysis on "${targetUrl}"`
          : `${toolType} analysis tools`,
        capabilities: [
          'Technical SEO audits',
          'Page speed testing',
          'Structured data validation',
          'Meta tag optimization'
        ]
      };
    }
    
    // 🔍 Keyword Analysis Context
    if (pathname.includes('/keywords') || pathname.includes('/keyword-analysis')) {
      const analysisData = getAnalysisData();
      const keywordData = analysisData?.keywords || null;
      
      return {
        type: 'keyword_analysis', 
        page: null,
        data: {
          keywords: keywordData,
          totalKeywords: keywordData?.length || 0,
          topKeywords: keywordData?.slice(0, 10) || []
        },
        summary: `Keyword analysis (${keywordData?.length || 0} keywords tracked)`,
        capabilities: [
          'Keyword performance tracking',
          'Search volume analysis', 
          'Competitor keyword gaps',
          'SERP position monitoring'
        ]
      };
    }
    
    // ⚙️ Settings/Configuration Context
    if (pathname.includes('/settings') || pathname.includes('/subscription')) {
      const userInfo = authStatus?.user || {};
      
      return {
        type: 'settings_configuration',
        page: null,
        data: {
          user: userInfo,
          subscription: userInfo.subscription_tier || 'none',
          isDemo: userInfo.is_demo_user || false,
          gscConnected: appData?.isConnected || false
        },
        summary: 'Account settings and configuration',
        capabilities: [
          'Google Search Console setup',
          'Subscription management',
          'User preferences',
          'Integration configuration'
        ]
      };
    }
    
    // 🏗️ Onboarding Context
    if (pathname.includes('/onboarding')) {
      return {
        type: 'onboarding_setup',
        page: null,
        data: {
          step: searchParams.get('step') || 'start',
          isFirstTime: true
        },
        summary: 'Getting started with QueryBurst',
        capabilities: [
          'Initial setup guidance',
          'Google Search Console connection',
          'First analysis walkthrough',
          'Feature introduction'
        ]
      };
    }
    
    // 📄 Default/Unknown Context
    return {
      type: 'general_consultation',
      page: null,
      data: {
        property: getPropertyInfo(),
        analysis: getAnalysisData(),
        hasAnyData: !!getAnalysisData()
      },
      summary: 'General SEO consultation',
      capabilities: [
        'SEO strategy guidance',
        'Performance analysis',
        'Technical recommendations',
        'Report navigation help'
      ]
    };
    
  }, [location.pathname, urlParams, appData, authStatus]);
  
  // Track context changes for conversation continuity
  useEffect(() => {
    setContextHistory(prev => {
      // Only add to history if context type actually changed
      const lastContext = prev[prev.length - 1];
      if (!lastContext || lastContext.type !== currentContext.type || lastContext.page !== currentContext.page) {
        const newHistory = [...prev, {
          ...currentContext,
          timestamp: new Date(),
          pathname: location.pathname
        }];
        
        // Keep only last 10 context changes
        return newHistory.slice(-10);
      }
      return prev;
    });
  }, [currentContext, location.pathname]);
  
  // Helper functions for context utilities
  const contextUtils = {
    // Check if user is looking at a specific page
    isAnalyzingPage: () => currentContext.type === 'page_analysis' && currentContext.page,
    
    // Get current page being analyzed
    getCurrentPage: () => currentContext.page,
    
    // Check if analysis data is available
    hasAnalysisData: () => !!currentContext.data?.analysis,
    
    // Get quick stats for current context
    getQuickStats: () => {
      const { data, type } = currentContext;
      
      if (type === 'site_overview' && data.analysis) {
        return {
          totalPages: data.totalPages,
          totalClicks: data.analysis.meta?.globalSummary?.totalClicksNew || 0,
          avgPosition: data.analysis.meta?.globalSummary?.averagePositionNew || null
        };
      }
      
      if (type === 'page_analysis' && data.pageData) {
        return {
          clicks: data.clicks,
          impressions: data.impressions,
          position: data.position,
          change: data.clicksChange
        };
      }
      
      return null;
    },
    
    // Get suggested actions for current context
    getSuggestedActions: () => {
      const { type, data, subType } = currentContext;
      
      // Page-specific suggestions based on current tab
      if (type === 'page_analysis' && subType) {
        const tabSuggestions = {
          'overview': [
            'How is this page performing overall?',
            'What are the main SEO issues with this page?',
            'How can I improve this page\'s rankings?',
            'Show me this page\'s keyword opportunities'
          ],
          'keywords': [
            'Which keywords is this page ranking for?',
            'How can I improve keyword rankings?',
            'What new keywords should I target?',
            'Show me keyword opportunities for this page'
          ],
          'lighthouse': [
            'How fast does this page load?',
            'What are the Core Web Vitals issues?',
            'How can I improve page speed?',
            'Check this page\'s mobile performance'
          ],
          'search-intent': [
            'Does this page match search intent?',
            'How can I better target user intent?',
            'What content is missing for my keywords?',
            'Analyze competing pages for this intent'
          ],
          'quality-rater': [
            'How does this page score on quality?',
            'What E-A-T signals can I improve?',
            'How trustworthy does this page appear?',
            'Review this page against quality guidelines'
          ],
          'helpful-content': [
            'Is this content helpful to users?',
            'How can I make this content more valuable?',
            'Does this page satisfy user needs?',
            'Improve this page\'s helpfulness score'
          ],
          'structured-v2': [
            'What structured data should I add?',
            'Are there schema markup opportunities?',
            'How can I improve rich snippets?',
            'Validate this page\'s structured data'
          ],
          'content-gaps': [
            'What content is missing from this page?',
            'How does this compare to competitors?',
            'What topics should I add?',
            'Find content gaps for my keywords'
          ],
          'seo-audit': [
            'What are this page\'s technical issues?',
            'Run a complete SEO audit',
            'Check for crawling problems',
            'Identify technical optimization opportunities'
          ],
          'seo-analysis': [
            'Get AI recommendations for this page',
            'Analyze this page comprehensively',
            'What should I prioritize fixing?',
            'Get a content strategy for this page'
          ]
        };
        
        return tabSuggestions[subType] || [
          'How can I improve this page\'s ranking?',
          'What keywords is this page ranking for?',
          'Run a content audit on this page',
          'Compare this page to competitors'
        ];
      }
      
      const suggestions = {
        'ai_tool_prompt_burst': [
          'What did the prompt burst analysis find?',
          'Explain the query decomposition results',
          'What content gaps were identified?',
          'How can I optimize for these AI search patterns?'
        ],
        'ai_tool_ai_crawler': [
          'What did the AI crawler analysis reveal?',
          'What concepts are missing from this page?',
          'How can I improve AI comprehension?',
          'What optimization recommendations do you have?'
        ],
        'site_overview': [
          'Analyze my top performing pages',
          'Show me my biggest traffic changes',
          'What should I focus on improving?',
          'Run a site-wide technical audit'
        ],
        'page_analysis': [
          'How can I improve this page\'s ranking?',
          'What keywords is this page ranking for?',
          'Compare this page to my homepage',
          'Run a content audit on this page'
        ],
        'historical_analysis': [
          'What caused my traffic changes?',
          'Show me seasonal trends',
          'Compare to last year',
          'Identify my growth opportunities'
        ],
        'tools_analysis': [
          'Check my page speed score',
          'Audit technical SEO issues',
          'Validate my structured data',
          'Review my meta tags'
        ],
        'keyword_analysis': [
          'Which keywords are declining?',
          'Find new keyword opportunities',
          'Analyze competitor keywords',
          'Check my ranking progress'
        ]
      };
      
      return suggestions[type] || [
        'Analyze my website performance',
        'What are my biggest SEO opportunities?',
        'Help me prioritize my SEO efforts',
        'Show me my recent changes'
      ];
    },
    
    // Get context transition message
    getContextTransition: () => {
      const recent = contextHistory.slice(-2);
      if (recent.length >= 2) {
        const [previous, current] = recent;
        return {
          from: previous.type,
          to: current.type,
          message: `Switched from ${previous.summary} to ${current.summary}`
        };
      }
      return null;
    }
  };
  
  return {
    ...currentContext,
    history: contextHistory,
    utils: contextUtils
  };
};
